// eslint-disable-next-line import/no-unassigned-import
import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { v4 as UUIDv4 } from 'uuid';

import { EnableClassTelemetry } from 'app/services/otel.service';
import { QueryParams, WrappedRequest } from '../api.service';
import { createCache } from '../cache';
import { WithAuditContext } from '../http-mutating-operations.interceptor';
import { SemantriaAPIService } from '../semantria-api.service';

export interface BaseConfiguration {
  id: string;
  name: string;
  settings: string[];
  language_id: string;
  language_name: string;
  modification_date: Date;
  creator_user_id: string;
  alphanumeric_threshold: number;
  concept_topics_threshold: number; // float
  entities_threshold: number;
  summary_size: number;
  is_autoresponse_enabled: boolean;
  callback_url?: string;
  version?: string;
  sentiment_sections?: Array<string>;
}

export interface SemantriaConfiguration extends BaseConfiguration {
  creation_date: Date;
  last_processing_date: Date;
  group: string;
  pos_types?: string;
  template_config?: string;
  cloned_from?: string;
  notes?: string;
  tags?: string[];
  deep_models?: string[];
  deep_models_threshold?: number;
  removed?: boolean;
  evaluation?: boolean;
  config_routes?: { config_route_id: string; config_route_name: string }[];
  layer?: boolean;
  parents?: string[];
}

export interface SemantriaConfigurationDto extends SemantriaConfiguration {
  blacklist_items: Partial<ConfigurationItem>[];
  concept_topics: Partial<ConceptTopicItem>[];
  model_refs: { instance_id: string; model_id: string }[];
  query_topics: Partial<QueryTopicItem>[];
  sentiment_phrases: Partial<SentimentItem>[];
  taxonomies: Partial<TaxonomyNodeItem>[];
  user_entities: Partial<UserEntityItem>[];
}

export interface ConfigurationItemResult<T> {
  data: T[];
  page: number;
  page_size: number;
  total_elements: number;
  total_pages: number;
}

export interface ConfigurationItemSortParams {
  sort: 'name' | 'last_updated';
  reverseOrder?: boolean;
}

export interface ConfigurationItem {
  id: string;
  name: string;
  modification_date?: Date;
  cloned_from?: string;
  config_id?: string;
  properties?: { [key: string]: string };
}

export interface SentimentItem extends ConfigurationItem {
  weight: number;
}

export interface QueryTopicItem extends ConfigurationItem {
  query: string;
  hidden?: boolean;
  purpose?: string;
  channels?: string[];
}

export interface ConceptTopicItem extends SentimentItem {
  samples: string[];
}

export interface UserEntityItem extends ConfigurationItem {
  type: string;
  label: string;
  normalized: string;
}

export interface TaxonomyNodeItem extends ConfigurationItem {
  children?: TaxonomyNodeItem[];
  elements: TaxonomyElement[];
  enforce_parent_matching: boolean;
}

export interface TaxonomyElement {
  target_entity_type?: string;
  target_id: string;
  target_type: string;
  uuid?: string;
}

export interface TaxonomyTargets {
  user_entities: string[];
  concept_topics: string[];
  query_topics: string[];
}

export interface ConfigFilters {
  scope: string;
  projectId?: string;
  language: string;
  showDeleted: boolean;
  configRouteId?: string;
}

export interface ConfigListSettings {
  filter: ConfigFilters;
  sort: string;
}

@EnableClassTelemetry()
@Injectable()
export class ConfigurationService extends SemantriaAPIService {
  readonly CONFIG_LIST_SETTINGS_KEY = 'lex-config-list-settings';
  private readonly ALL_GROUP_CONFIGURATIONS_KEY = '<none>';
  private readonly cache = createCache<string, SemantriaConfiguration[]>(100);

  private createEvalConfigRequestMap = new Map<string, WrappedRequest<string>>();

  private buildItemListParams(
    page: number,
    pageSize: number,
    sortParams: ConfigurationItemSortParams
  ): QueryParams {
    const params: QueryParams = {
      page_number: page,
      page_size: pageSize,
      sort: sortParams.sort,
    };
    if (sortParams.reverseOrder != null) {
      params['reverse_order'] = sortParams.reverseOrder;
    }
    return params;
  }

  clearCache() {
    this.cache.clear();
  }

  groupUpdated(groupId: string) {
    this.cache.delete(groupId);
    this.cache.delete(this.ALL_GROUP_CONFIGURATIONS_KEY);
  }

  async getConfigurations(deleted?: boolean, groupId?: string): Promise<SemantriaConfiguration[]> {
    let configs: SemantriaConfiguration[];

    const key = groupId || this.ALL_GROUP_CONFIGURATIONS_KEY;
    if (this.cache.has(key)) {
      configs = this.cache.get(key);
    } else {
      // always ask for deleted here to make caching more effective
      const params = {
        with_deleted: true,
        group_id: groupId,
      };
      configs = await this.get<SemantriaConfiguration[]>('/configs/', params);
      this.cache.set(key, configs);
    }
    if (!deleted) {
      configs = configs.filter((c) => !c.removed);
    }
    return configs;
  }

  getConfiguration(configId: string): Promise<SemantriaConfiguration> {
    return this.get<SemantriaConfiguration>(`/configs/${configId}`);
  }

  getConfigurationParents(
    configId: string,
    resultType?: 'tree' | 'list'
  ): Promise<{ tree: any; list: any }> {
    const params = {
      result_type: resultType,
    };

    return this.get(`/configs/${configId}/ancestors/`, params);
  }

  getConfigurationChildren(configId: string): Promise<string[]> {
    return this.get(`/configs/${configId}/children/`);
  }

  @WithAuditContext('configuration-created', (_, args) => {
    return { name: args[0]?.name, group_id: args[1] };
  })
  createConfiguration(
    config: Partial<SemantriaConfiguration>,
    groupId: string
  ): Promise<SemantriaConfiguration> {
    this.clearCache();
    if (groupId) {
      return this.post<SemantriaConfiguration>(
        '/configs/',
        { ...config },
        {},
        { group_id: groupId }
      );
    }
    return this.post<SemantriaConfiguration>('/configs/', { ...config });
  }

  @WithAuditContext('configuration-created', (_, args) => {
    return { name: args[0]?.name, group_id: args[1], evaluation: 'true' };
  })
  createEvaluationConfiguration(
    config: SemantriaConfiguration,
    groupId: string
  ): Promise<SemantriaConfiguration> {
    return this.post<SemantriaConfiguration>(
      '/configs/',
      { ...config },
      {},
      { evaluation: true, group_id: groupId }
    );
  }

  @WithAuditContext('configuration-updated', (instance: ConfigurationService, args) => {
    return {
      name: args[0].name,
      id: args[0].id,
      evaluationConfigId: instance?.getEvaluationConfigMap()?.[args[0].id],
    };
  })
  async updateConfiguration(config: SemantriaConfiguration): Promise<SemantriaConfiguration> {
    this.clearCache();
    const evaluationConfigMap = this.getEvaluationConfigMap();
    if (config.layer && evaluationConfigMap.hasOwnProperty(config.id)) {
      const evaluationConfig = await this.getConfiguration(evaluationConfigMap[config.id]);
      evaluationConfig.parents = [...config.parents];
      await this.put(`/configs/${evaluationConfig.id}`, { ...evaluationConfig });
    }
    return this.put(`/configs/${config.id}`, { ...config });
  }

  @WithAuditContext('configuration-deleted', (instance: ConfigurationService, args) => {
    return { configId: args[0], evaluationConfigId: instance?.getEvaluationConfigMap()?.[args[0]] };
  })
  async deleteConfiguration(configId: string): Promise<void> {
    this.clearCache();
    const evaluationConfigMap = this.getEvaluationConfigMap();
    if (evaluationConfigMap.hasOwnProperty(configId)) {
      await this.delete(`/configs/${evaluationConfigMap[configId]}`);
      delete evaluationConfigMap[configId];
      this.storeEvaluationConfigMap(evaluationConfigMap);
    }
    return this.delete(`/configs/${configId}`);
  }

  @WithAuditContext('configuration-tag-added', (_, args) => {
    return { groupId: args[0], configId: args[1], tag: args[2] };
  })
  addTag(groupId: string, configId: string, tag: string) {
    this.groupUpdated(groupId);
    return this.patch(`/configs/${configId}/tags?action=add`, [tag]);
  }

  @WithAuditContext('configuration-tag-removed', (_, args) => {
    return { groupId: args[0], configId: args[1], tag: args[2] };
  })
  removeTag(groupId: string, configId: string, tag: string) {
    this.groupUpdated(groupId);
    return this.patch(`/configs/${configId}/tags?action=delete`, [tag]);
  }

  exportConfiguration(configId: string): Promise<SemantriaConfigurationDto> {
    return this.get(`/configs/${configId}/export`);
  }

  @WithAuditContext('configuration-imported', (_, args) => {
    return { groupId: args[1], configId: args[0].id, force: args[2] };
  })
  importConfiguration(config: SemantriaConfigurationDto, groupId: string, force = false) {
    this.clearCache();
    return this.post(`/configs/import`, { ...config }, {}, { group_id: groupId, force });
  }

  getUserEntities(
    configId: string,
    page: number = 0,
    pageSize: number = 10,
    search?: string,
    searchField?: string,
    sortParams: ConfigurationItemSortParams = { sort: 'name' }
  ): Promise<ConfigurationItemResult<UserEntityItem>> {
    const params = this.buildItemListParams(page, pageSize, sortParams);
    if (search) {
      params['search'] = search;
      if (searchField !== 'all') {
        params['search_field'] = searchField;
      }
    }

    return this.get(`/configs/${configId}/user-entities/`, params);
  }

  @WithAuditContext('configuration-user-entity-deleted', (_, args) => {
    return { configId: args[0], id: args[1] };
  })
  deleteUserEntity(configId: string, entityId: string): Promise<UserEntityItem> {
    return this.delete(`/configs/${configId}/user-entities/${entityId}`);
  }

  @WithAuditContext('configuration-user-entities-deleted', (_, args) => {
    return { configId: args[0], ids: args[1] };
  })
  deleteBatchUserEntities(configId: string, entityIds: string[]): Promise<UserEntityItem[]> {
    return this.delete(
      `/configs/${configId}/user-entities/batch/`,
      undefined,
      undefined,
      entityIds
    );
  }

  @WithAuditContext('configuration-all-user-entities-deleted', (_, args) => {
    return { configId: args[0] };
  })
  deleteAllUserEntities(configId: string): Promise<string[]> {
    return this.delete(`/configs/${configId}/user-entities/`);
  }

  @WithAuditContext('configuration-user-entity-created', (_, args) => {
    return { configId: args[0], name: args[1].name };
  })
  createUserEntity(configId: string, entity: ConfigurationItem): Promise<UserEntityItem> {
    return this.post(`/configs/${configId}/user-entities/`, { ...entity });
  }

  @WithAuditContext('configuration-user-entities-created', (_, args) => {
    return {
      configId: args[0],
      names: (args[1] as ConfigurationItem[]).map((entity) => entity.name),
    };
  })
  createBatchUserEntities(
    configId: string,
    entities: ConfigurationItem[]
  ): Promise<UserEntityItem[]> {
    return this.post(`/configs/${configId}/user-entities/batch/`, entities);
  }

  @WithAuditContext('configuration-user-entity-updated', (_, args) => {
    return { configId: args[0], name: args[1].name, id: args[1].id };
  })
  saveUserEntity(configId: string, entity: ConfigurationItem): Promise<UserEntityItem> {
    return this.put(`/configs/${configId}/user-entities/${entity.id}`, { ...entity });
  }

  @WithAuditContext('configuration-user-entities-updated', (_, args) => {
    return {
      configId: args[0],
      entities: (args[1] as ConfigurationItem[]).map((entity) => ({
        name: entity.name,
        id: entity.id,
      })),
    };
  })
  saveBatchUserEntities(
    configId: string,
    entities: ConfigurationItem[]
  ): Promise<UserEntityItem[]> {
    return this.put(`/configs/${configId}/user-entities/batch/`, entities);
  }

  getUserEntityByClonedFrom(configId: string, clonedFrom: string): Promise<UserEntityItem> {
    return this.get(`/configs/${configId}/user-entities/cloned_from/${clonedFrom}`);
  }

  getSentimentPhrases(
    configId: string,
    page: number = 0,
    pageSize: number = 10,
    search?: string,
    searchField?: string,
    sortParams: ConfigurationItemSortParams = { sort: 'name' }
  ): Promise<ConfigurationItemResult<SentimentItem>> {
    const params = this.buildItemListParams(page, pageSize, sortParams);
    if (search) {
      params['search'] = search;

      if (searchField === 'name') {
        params['search_field'] = 'name';
      }
    }

    return this.get(`/configs/${configId}/sentiment-phrases/`, params);
  }

  @WithAuditContext('configuration-phrase-deleted', (_, args) => {
    return { configId: args[0], id: args[1] };
  })
  deleteSentimentPhrase(configId: string, phraseId: string): Promise<SentimentItem> {
    return this.delete(`/configs/${configId}/sentiment-phrases/${phraseId}`);
  }

  @WithAuditContext('configuration-phrases-deleted', (_, args) => {
    return { configId: args[0], ids: args[1] };
  })
  deleteBatchSentimentPhrases(configId: string, phraseIds: string[]): Promise<SentimentItem[]> {
    return this.delete(
      `/configs/${configId}/sentiment-phrases/batch/`,
      undefined,
      undefined,
      phraseIds
    );
  }

  @WithAuditContext('configuration-all-phrases-deleted', (_, args) => {
    return { configId: args[0] };
  })
  deleteAllSentimentPhrases(configId: string): Promise<string[]> {
    return this.delete(`/configs/${configId}/sentiment-phrases/`);
  }

  @WithAuditContext('configuration-phrase-created', (_, args) => {
    return { configId: args[0], name: args[1].name };
  })
  createSentimentPhrase(configId: string, phrase: SentimentItem): Promise<SentimentItem> {
    return this.post(`/configs/${configId}/sentiment-phrases/`, { ...phrase });
  }

  @WithAuditContext('configuration-phrases-created', (_, args) => {
    return {
      configId: args[0],
      names: (args[1] as SentimentItem[]).map((phrase) => phrase.name),
    };
  })
  createBatchSentimentPhrases(
    configId: string,
    phrases: SentimentItem[]
  ): Promise<SentimentItem[]> {
    return this.post(`/configs/${configId}/sentiment-phrases/batch/`, phrases);
  }

  @WithAuditContext('configuration-phrase-updated', (_, args) => {
    return { configId: args[0], name: args[1].name, id: args[1].id };
  })
  saveSentimentPhrase(configId: string, phrase: SentimentItem): Promise<SentimentItem> {
    return this.put(`/configs/${configId}/sentiment-phrases/${phrase.id}`, { ...phrase });
  }

  @WithAuditContext('configuration-phrases-updated', (_, args) => {
    return {
      configId: args[0],
      phrases: (args[1] as SentimentItem[]).map((phrase) => ({ name: phrase.name, id: phrase.id })),
    };
  })
  saveBatchSentimentPhrases(configId: string, phrases: SentimentItem[]): Promise<SentimentItem[]> {
    return this.put(`/configs/${configId}/sentiment-phrases/batch/`, phrases);
  }

  getSentimentPhraseByClonedFrom(configId: string, clonedFrom: string): Promise<ConfigurationItem> {
    return this.get(`/configs/${configId}/sentiment-phrases/cloned_from/${clonedFrom}`);
  }

  getQueryTopics(
    configId: string,
    page: number = 0,
    pageSize: number = 10,
    search?: string,
    searchField?: string,
    sortParams: ConfigurationItemSortParams = { sort: 'name' }
  ): Promise<ConfigurationItemResult<QueryTopicItem>> {
    const params = this.buildItemListParams(page, pageSize, sortParams);
    if (search) {
      params['search'] = search;

      if (searchField === 'name') {
        params['search_field'] = 'name';
      }
      if (searchField === 'query') {
        params['search_field'] = 'query';
      }
    }

    return this.get(`/configs/${configId}/query-topics/`, params);
  }

  getQueryTopic(configId: string, topicId: string): Promise<QueryTopicItem> {
    return this.get(`/configs/${configId}/query-topics/${topicId}`);
  }

  @WithAuditContext('configuration-topic-deleted', (_, args) => {
    return { configId: args[0], id: args[1] };
  })
  deleteQueryTopic(configId: string, topicId: string): Promise<QueryTopicItem> {
    return this.delete(`/configs/${configId}/query-topics/${topicId}`);
  }

  @WithAuditContext('configuration-topics-deleted', (_, args) => {
    return { configId: args[0], ids: args[1] };
  })
  deleteBatchQueryTopics(configId: string, topicIds: string[]): Promise<QueryTopicItem[]> {
    return this.delete(`/configs/${configId}/query-topics/batch/`, undefined, undefined, topicIds);
  }

  @WithAuditContext('configuration-all-topics-deleted', (_, args) => {
    return { configId: args[0] };
  })
  deleteAllQueryTopics(configId: string): Promise<string[]> {
    return this.delete(`/configs/${configId}/query-topics/`);
  }

  @WithAuditContext('configuration-topic-created', (_, args) => {
    return { configId: args[0], name: args[1].name, query: args[1].query };
  })
  createQueryTopic(configId: string, topic: QueryTopicItem): Promise<QueryTopicItem> {
    return this.post(`/configs/${configId}/query-topics/`, { ...topic });
  }

  @WithAuditContext('configuration-topics-created', (_, args) => {
    return {
      configId: args[0],
      topics: args[1].map((topic: QueryTopicItem) => ({ name: topic.name, id: topic.id })),
    };
  })
  createBatchQueryTopics(configId: string, topics: QueryTopicItem[]): Promise<QueryTopicItem> {
    return this.post(`/configs/${configId}/query-topics/batch/`, topics);
  }

  getQueryTopicByClonedFrom(configId: string, clonedFrom: string): Promise<ConfigurationItem> {
    return this.get(`/configs/${configId}/query-topics/cloned_from/${clonedFrom}`);
  }

  @WithAuditContext('configuration-topic-updated', (_, args) => {
    return { configId: args[0], id: args[1].id, name: args[1].name, query: args[1].query };
  })
  saveQueryTopic(configId: string, topic: QueryTopicItem): Promise<QueryTopicItem> {
    return this.put(`/configs/${configId}/query-topics/${topic.id}`, { ...topic });
  }

  @WithAuditContext('configuration-topics-updated', (_, args) => {
    return {
      configId: args[0],
      topics: args[1].map((topic: QueryTopicItem) => ({ name: topic.name, id: topic.id })),
    };
  })
  saveBatchQueryTopics(configId: string, topics: QueryTopicItem[]): Promise<QueryTopicItem[]> {
    return this.put(`/configs/${configId}/query-topics/batch/`, topics);
  }

  getConceptTopics(
    configId: string,
    page: number = 0,
    pageSize: number = 10,
    search?: string,
    searchField?: string,
    sortParams: ConfigurationItemSortParams = { sort: 'name' }
  ): Promise<ConfigurationItemResult<ConceptTopicItem>> {
    const params = this.buildItemListParams(page, pageSize, sortParams);
    if (search) {
      params['search'] = search;

      if (searchField === 'name') {
        params['search_field'] = 'name';
      }
      if (searchField === 'query') {
        params['search_field'] = 'sample';
      }
    }

    return this.get(`/configs/${configId}/concept-topics/`, params);
  }

  getConceptTopic(configId: string, topicId: string): Promise<ConceptTopicItem> {
    return this.get(`/configs/${configId}/concept-topics/${topicId}`);
  }

  @WithAuditContext('configuration-concept-topic-deleted', (_, args) => {
    return { configId: args[0], id: args[1] };
  })
  deleteConceptTopic(configId: string, topicId: string): Promise<ConceptTopicItem> {
    return this.delete(`/configs/${configId}/concept-topics/${topicId}`);
  }

  @WithAuditContext('configuration-concept-topics-deleted', (_, args) => {
    return { configId: args[0], ids: args[1] };
  })
  deleteBatchConceptTopics(configId: string, topicIds: string[]): Promise<ConceptTopicItem[]> {
    return this.delete(
      `/configs/${configId}/concept-topics/batch/`,
      undefined,
      undefined,
      topicIds
    );
  }

  @WithAuditContext('configuration-all-concept-topics-deleted', (_, args) => {
    return { configId: args[0] };
  })
  deleteAllConceptTopics(configId: string): Promise<string[]> {
    return this.delete(`/configs/${configId}/concept-topics/`);
  }

  @WithAuditContext('configuration-concept-topic-created', (_, args) => {
    return { configId: args[0], name: args[1].name, samples: args[1].samples };
  })
  createConceptTopic(configId: string, topic: ConceptTopicItem): Promise<ConceptTopicItem> {
    return this.post(`/configs/${configId}/concept-topics/`, { ...topic });
  }

  @WithAuditContext('configuration-concept-topics-created', (_, args) => {
    return {
      configId: args[0],
      topics: args[1].map((topic: ConceptTopicItem) => ({
        name: topic.name,
        samples: topic.samples,
      })),
    };
  })
  createBatchConceptTopics(
    configId: string,
    topics: ConceptTopicItem[]
  ): Promise<ConceptTopicItem> {
    return this.post(`/configs/${configId}/concept-topics/batch/`, topics);
  }

  getConceptTopicByClonedFrom(configId: string, topicId: string): Promise<ConfigurationItem> {
    return this.get(`/configs/${configId}/concept-topics/cloned_from/${topicId}`);
  }

  @WithAuditContext('configuration-concept-topic-updated', (_, args) => {
    return { configId: args[0], id: args[1].id, name: args[1].name, samples: args[1].samples };
  })
  saveConceptTopic(configId: string, topic: ConceptTopicItem): Promise<ConceptTopicItem> {
    return this.put(`/configs/${configId}/concept-topics/${topic.id}`, { ...topic });
  }

  @WithAuditContext('configuration-concept-topics-updated', (_, args) => {
    return {
      configId: args[0],
      topics: args[1].map((topic: ConceptTopicItem) => ({
        id: topic.id,
        name: topic.name,
        samples: topic.samples,
      })),
    };
  })
  saveBatchConceptTopics(
    configId: string,
    topics: ConceptTopicItem[]
  ): Promise<ConceptTopicItem[]> {
    return this.put(`/configs/${configId}/concept-topics/batch/`, topics);
  }

  getTaxonomies(
    configId: string,
    page: number = 0,
    pageSize: number = 10
  ): Promise<ConfigurationItemResult<TaxonomyNodeItem>> {
    const params = {
      page_number: page,
      page_size: pageSize,
      sort: 'name',
    };

    return this.get(`/configs/${configId}/taxonomy/`, params);
  }

  @WithAuditContext('configuration-taxonomy-deleted', (_, args) => {
    return { configId: args[0], id: args[1] };
  })
  deleteTaxonomyNode(configId: string, nodeId: string): Promise<TaxonomyNodeItem> {
    return this.delete(`/configs/${configId}/taxonomy/${nodeId}`);
  }

  @WithAuditContext('configuration-taxonomies-deleted', (_, args) => {
    return { configId: args[0], ids: args[1] };
  })
  deleteBatchTaxonomyNodes(configId: string, nodeIds: string[]): Promise<TaxonomyNodeItem> {
    return this.delete(`/configs/${configId}/taxonomy/batch/`, undefined, undefined, nodeIds);
  }

  @WithAuditContext('configuration-all-taxonomies-deleted', (_, args) => {
    return { configId: args[0] };
  })
  deleteAllTaxonomyNodes(configId: string): Promise<string[]> {
    return this.delete(`/configs/${configId}/taxonomy/`);
  }

  @WithAuditContext('configuration-taxonomy-created', (_, args) => {
    return { configId: args[0], name: args[1].name };
  })
  createTaxonomyNode(configId: string, node: ConfigurationItem): Promise<TaxonomyNodeItem> {
    return this.post(`/configs/${configId}/taxonomy/`, { ...node });
  }

  @WithAuditContext('configuration-taxonomies-created', (_, args) => {
    return {
      configId: args[0],
      names: args[1].map((node: ConfigurationItem) => node.name),
    };
  })
  createBatchTaxonomyNodes(
    configId: string,
    nodes: ConfigurationItem[]
  ): Promise<TaxonomyNodeItem[]> {
    return this.post(`/configs/${configId}/taxonomy/batch/`, nodes);
  }

  @WithAuditContext('configuration-taxonomy-updated', (_, args) => {
    return { configId: args[0], id: args[1].id, name: args[1].name };
  })
  saveTaxonomyNode(configId: string, node: ConfigurationItem): Promise<TaxonomyNodeItem> {
    return this.put(`/configs/${configId}/taxonomy/${node.id}`, { ...node });
  }

  @WithAuditContext('configuration-taxonomies-updated', (_, args) => {
    return {
      configId: args[0],
      nodes: args[1].map((node: ConfigurationItem) => ({ id: node.id, name: node.name })),
    };
  })
  saveBatchTaxonomyNodes(
    configId: string,
    nodes: ConfigurationItem[]
  ): Promise<TaxonomyNodeItem[]> {
    return this.put(`/configs/${configId}/taxonomy/batch/`, nodes);
  }

  getBlacklists(
    configId: string,
    page: number = 0,
    pageSize: number = 10,
    search?: string,
    searchField?: string,
    sortParams: ConfigurationItemSortParams = { sort: 'name' }
  ): Promise<ConfigurationItemResult<ConfigurationItem>> {
    const params = this.buildItemListParams(page, pageSize, sortParams);
    if (search) {
      params['search'] = search;

      if (searchField === 'name') {
        params['search_field'] = 'name';
      }
    }

    return this.get(`/configs/${configId}/blacklist/`, params);
  }

  @WithAuditContext('configuration-blacklist-entry-deleted', (_, args) => {
    return { configId: args[0], id: args[1] };
  })
  deleteBlacklistEntry(configId: string, entryId: string): Promise<ConfigurationItem> {
    return this.delete(`/configs/${configId}/blacklist/${entryId}`);
  }

  @WithAuditContext('configuration-blacklist-entries-deleted', (_, args) => {
    return { configId: args[0], ids: args[1] };
  })
  deleteBatchBlacklistEntries(configId: string, entries: string[]): Promise<ConfigurationItem> {
    return this.delete(`/configs/${configId}/blacklist/batch/`, undefined, undefined, entries);
  }

  @WithAuditContext('configuration-all-blacklist-entries-deleted', (_, args) => {
    return { configId: args[0] };
  })
  deleteAllBlacklistEntries(configId: string): Promise<string[]> {
    return this.delete(`/configs/${configId}/blacklist/`);
  }

  @WithAuditContext('configuration-blacklist-entry-created', (_, args) => {
    return { configId: args[0], name: args[1].name };
  })
  createBlacklistEntry(configId: string, entry: ConfigurationItem): Promise<ConfigurationItem> {
    return this.post(`/configs/${configId}/blacklist/`, { ...entry });
  }

  @WithAuditContext('configuration-blacklist-entries-created', (_, args) => {
    return { configId: args[0], names: args[1].map((node: ConfigurationItem) => node.name) };
  })
  createBatchBlacklistEntries(
    configId: string,
    entries: ConfigurationItem[]
  ): Promise<ConfigurationItem[]> {
    return this.post(`/configs/${configId}/blacklist/batch/`, entries);
  }

  @WithAuditContext('configuration-blacklist-entry-updated', (_, args) => {
    return { configId: args[0], id: args[1].id, name: args[1].name };
  })
  saveBlacklistEntry(configId: string, entry: ConfigurationItem): Promise<ConfigurationItem> {
    return this.put(`/configs/${configId}/blacklist/${entry.id}`, { ...entry });
  }

  @WithAuditContext('configuration-blacklist-entries-updated', (_, args) => {
    return {
      configId: args[0],
      entries: args[1].map((node: ConfigurationItem) => ({ id: node.id, name: node.name })),
    };
  })
  saveBatchBlacklistEntries(
    configId: string,
    entries: ConfigurationItem[]
  ): Promise<ConfigurationItem[]> {
    return this.put(`/configs/${configId}/blacklist/batch/`, entries);
  }

  getTaxonomyItems(configId: string): Promise<TaxonomyTargets> {
    return this.get(`/configs/${configId}/taxonomy-items/`);
  }

  getConfigurationItemCounts(configId: string): Promise<any> {
    return this.get(`/configs/${configId}/info/`);
  }

  async getEvaluationConfigId(configuration: SemantriaConfiguration): Promise<string> {
    const evalConfig = await this.getEvaluationConfig(configuration);
    return evalConfig.id;
  }

  async getEvaluationConfig(
    configuration: SemantriaConfiguration
  ): Promise<SemantriaConfiguration> {
    configuration.concept_topics_threshold = 0.2;
    let evaluationConfig;
    let invalidateEvaluationConfig: boolean = false;
    const evaluationConfigMap = this.getEvaluationConfigMap();

    if (this.hasEvaluationConfigInLocalStorage(configuration)) {
      try {
        evaluationConfig = await this.getConfiguration(evaluationConfigMap[configuration.id]);
        if (evaluationConfig) {
          const now = new Date();
          const hourAgo = new Date(now.getTime() - 1000 * 60 * 60);
          const evaluationConfigDate = new Date(evaluationConfig.updated);
          if (evaluationConfigDate < hourAgo) {
            invalidateEvaluationConfig = true;
          }
        }
      } catch (error) {
        //Do nothing if it's not found
      }
    }

    if (invalidateEvaluationConfig) {
      await this.deleteConfiguration(evaluationConfigMap[configuration.id]);
    }

    if (!evaluationConfig || invalidateEvaluationConfig) {
      delete evaluationConfigMap[configuration.id];
      evaluationConfig = await this.createEvaluationConfig(configuration);
    }

    return evaluationConfig;
  }

  hasEvaluationConfigInLocalStorage(configuration: SemantriaConfiguration): boolean {
    return this.getEvaluationConfigMap().hasOwnProperty(configuration.id);
  }

  getEvaluationConfigMap() {
    return this.localStorage.getItem('evaluationConfigMap') || JSON.parse('{}');
  }

  storeEvaluationConfigMap(evaluationConfigMap) {
    this.localStorage.setItem('evaluationConfigMap', evaluationConfigMap);
  }

  async updateEvaluationConfiguration(
    configuration: SemantriaConfiguration
  ): Promise<SemantriaConfiguration> {
    const evaluationConfig = await this.getEvaluationConfig(configuration);
    const evalConfigUpdate = {
      ...JSON.parse(JSON.stringify(configuration)),
      id: evaluationConfig.id,
      name: evaluationConfig.name,
    };
    return this.updateConfiguration(evalConfigUpdate);
  }

  async createEvaluationConfig(
    configuration: SemantriaConfiguration
  ): Promise<SemantriaConfiguration> {
    const clone = JSON.parse(JSON.stringify(configuration));
    clone.name = 'Ev_' + clone.name + '_' + UUIDv4();
    clone.cloned_from = clone.id;
    delete clone.id;
    const groupId = this.localStorage.getItem('group');

    let createEvalConfigRequest = this.createEvalConfigRequestMap.get(configuration.id);
    if (!createEvalConfigRequest?.isInProgress()) {
      createEvalConfigRequest = new WrappedRequest(
        this.createEvaluationConfiguration(clone, groupId)
      );
      this.createEvalConfigRequestMap.set(configuration.id, createEvalConfigRequest);
    }

    const clonedConfig = await createEvalConfigRequest.request;

    this.storeEvaluationConfigMap({
      ...this.getEvaluationConfigMap(),
      [configuration.id]: clonedConfig.id,
    });
    return clonedConfig;
  }

  //Not async because it doesn't actually matter if it succeeds or not.
  clearEvaluationConfigs() {
    const evaluationConfigMap = this.getEvaluationConfigMap();
    for (const key in evaluationConfigMap) {
      if (evaluationConfigMap.hasOwnProperty(key)) {
        //linter complains without this
        try {
          this.deleteConfiguration(evaluationConfigMap[key]);
        } catch (e) {
          //don't need to do anything.  It either doesn't exist or will get cleaned eventually
        }
      }
    }
    this.localStorage.removeItem('evaluationConfigMap');
  }

  public getConfigName(
    configurations: Map<string, SemantriaConfiguration>,
    configName: string,
    configId: string
  ): string {
    if (configName) {
      return configName;
    }
    return configurations && configurations.has(configId)
      ? configurations.get(configId).name
      : 'N/A (possibly deleted)';
  }

  loadConfigurationListSettings(): ConfigListSettings {
    return this.localStorage.getItem(this.CONFIG_LIST_SETTINGS_KEY);
  }

  storeConfigurationListSettings(settings: ConfigListSettings) {
    this.localStorage.setItem(this.CONFIG_LIST_SETTINGS_KEY, settings);
  }

  configNameExists(name: string, language_id: string): Observable<boolean> {
    const params = {
      name,
      language: language_id,
    };

    return from(this.get<boolean>('/configs/_validate_name', params));
  }
}
